import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Users, HelpCircle, SkipForward, Loader2, Volume2, VolumeX, Scale } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { Footer } from "@/components/Footer";
import { EPISODES } from "@/components/Derive";
import { listenersAt, fmt } from "@/lib/radioSchedule";
import { TeaserShare } from "@/components/TeaserShare";
import { TopicForm } from "@/components/TopicForm";
import { SocialInvite } from "@/components/SocialInvite";
import { ChannelPicker, CHANNEL_META } from "@/components/RadioChannels";
import { useRadio, CHANNELS } from "@/lib/RadioContext";
import { RadioReactions } from "@/components/RadioReactions";
import { RadioSchedule } from "@/components/RadioSchedule";

const TXT = {
  fr: { live: "En direct", title: "Radio Cocoon", sub: "Les voix de Cocoon, en continu. Tout le monde écoute le même instant.", listen: "Écouter le direct", pause: "Pause", listeners: "esprits à l'écoute", next: "À suivre", jingle: "Radio Cocoon · la radio de la curiosité", jingleSub: "Petite respiration avant l'épisode suivant", left: "restant", debate: "Débattre au Procès des Idées", ep: "Épisode", back: "Retour", keep: "La radio continue pendant que tu explores Cocoon.", birth: "Lire la naissance de Radio Cocoon" },
  en: { live: "Live", title: "Radio Cocoon", sub: "The voices of Cocoon, non-stop. Everyone hears the same moment.", listen: "Listen live", pause: "Pause", listeners: "minds listening", next: "Up next", jingle: "Radio Cocoon · the radio of curiosity", jingleSub: "A short breath before the next episode", left: "left", debate: "Debate it in the Trial of Ideas", ep: "Episode", back: "Back", keep: "The radio keeps playing while you explore Cocoon.", birth: "Read the birth of Radio Cocoon" },
  es: { live: "En directo", title: "Radio Cocoon", sub: "Las voces de Cocoon, sin pausa. Todos escuchan el mismo instante.", listen: "Escuchar en directo", pause: "Pausa", listeners: "mentes escuchando", next: "A continuación", jingle: "Radio Cocoon · la radio de la curiosidad", jingleSub: "Una breve pausa antes del próximo episodio", left: "restante", debate: "Debatir en el Juicio de las Ideas", ep: "Episodio", back: "Volver", keep: "La radio sigue sonando mientras exploras Cocoon.", birth: "Leer el nacimiento de Radio Cocoon" },
  nl: { live: "Live", title: "Radio Cocoon", sub: "De stemmen van Cocoon, non-stop. Iedereen hoort hetzelfde moment.", listen: "Luister live", pause: "Pauze", listeners: "geesten luisteren", next: "Hierna", jingle: "Radio Cocoon · de radio van nieuwsgierigheid", jingleSub: "Even ademhalen voor de volgende aflevering", left: "resterend", debate: "Bespreek in het Proces der Ideeën", ep: "Aflevering", back: "Terug", keep: "De radio speelt door terwijl je Cocoon verkent.", birth: "Lees de geboorte van Radio Cocoon" },
};

const PLAY_HINT = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("play") === "1";

export const EP_BY_SLUG = Object.fromEntries(EPISODES.map((e, i) => [e.slug, { ...e, num: EPISODES.length - i }]));

export const channelListeners = (ch, now) => {
  const k = CHANNELS.indexOf(ch);
  return k <= 0 ? listenersAt(now) : Math.max(7, Math.round(listenersAt(now + k * 7919) * 0.35));
};

export default function Radio() {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = TXT[lang];
  const r = useRadio();
  useEffect(() => { r.ensureLoaded(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = r.pos;
  const cur = pos ? EP_BY_SLUG[r.currentSlug] : null;
  const nxt = pos ? EP_BY_SLUG[r.nextSlug] : null;
  const isJingle = pos?.kind === "jingle";
  const tt = (e) => (e ? e.title[lang] || e.title.fr : "");

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors" data-testid="radio-back-link">← {T.back}</Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 pt-28 pb-24">
        <LiveBanner T={T} />
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 mb-3" data-testid="radio-title">{T.title}</h1>
        <p className="text-foreground/60 text-sm sm:text-base mb-3">{T.sub}</p>
        <Link to="/radio/naissance" data-testid="radio-birth-link" className="inline-block mb-8 text-sm text-primary/85 hover:text-primary underline-offset-4 hover:underline">✦ {T.birth}</Link>
        <ChannelPicker channels={r.channels} current={r.channel} onPick={r.setChannel} lang={lang} />

        {!pos || !cur ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>
        ) : (
          <>
            <NowPlaying T={T} lang={lang} cur={cur} isJingle={isJingle} pos={pos} tt={tt} r={r}
              channelLabel={CHANNEL_META[r.channel].label[lang]} listeners={channelListeners(r.channel, r.now)} />
            {nxt && <UpNext T={T} ep={nxt} tt={tt} inSec={pos.length - pos.offset + (isJingle ? 0 : r.program.jingle)} />}
            <p className="mt-4 text-center text-xs text-foreground/40">{T.keep}</p>
            <RadioReactions channel={r.channel} slug={isJingle ? r.nextSlug : r.currentSlug} lang={lang} />
            <RadioSchedule program={r.program} pos={pos} now={r.now} lang={lang} byslug={EP_BY_SLUG} />
          </>
        )}
        <TopicForm />
        <SocialInvite />
        <TeaserShare />
      </main>
      <Footer />
    </div>
  );
}

const LiveBanner = ({ T }) => (
  <div className="flex items-center gap-3" data-testid="radio-live-banner">
    <span className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-3.5 py-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-red-300">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
      </span>
      {T.live}
    </span>
    <div className="flex-1 overflow-hidden h-px bg-gradient-to-r from-red-400/40 to-transparent" />
  </div>
);

const NowPlaying = ({ T, lang, cur, isJingle, pos, tt, r, channelLabel, listeners }) => (
  <div className="glass rounded-3xl border border-primary/20 p-5 sm:p-8 grid sm:grid-cols-[240px_1fr] gap-6 sm:gap-8 items-center" data-testid="radio-now-playing">
    <div className="relative mx-auto w-full max-w-[260px]">
      <AnimatePresence mode="wait">
        <motion.img key={isJingle ? "jingle" : cur.slug} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
          src={isJingle ? "/derive/radio/cover.jpg" : `/derive/covers/${cur.slug}.jpg`} alt={isJingle ? T.title : tt(cur)}
          className="w-full aspect-square rounded-2xl object-cover border border-primary/25 shadow-2xl" data-testid="radio-cover" />
      </AnimatePresence>
    </div>
    <div className="min-w-0">
      <p className="text-[0.7rem] uppercase tracking-[0.24em] text-primary/80 mb-2" data-testid="radio-current-channel">
        {channelLabel}{isJingle ? "" : ` · ${T.ep} ${cur.num}`}
      </p>
      {isJingle ? (
        <>
          <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-2" data-testid="radio-current-title">{T.jingle}</h2>
          <p className="text-foreground/55 text-sm mb-5">{T.jingleSub}</p>
        </>
      ) : (
        <>
          <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-3" data-testid="radio-current-title">{tt(cur)}</h2>
          {cur.q && (
            <p className="flex items-start gap-2 font-display italic text-primary/90 text-base sm:text-lg mb-5" data-testid="radio-current-question">
              <HelpCircle size={16} className="mt-1 flex-shrink-0" /> {cur.q[lang] || cur.q.fr}
            </p>
          )}
          {cur.q && <DebateButton T={T} question={cur.q[lang] || cur.q.fr} />}
        </>
      )}
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-2" data-testid="radio-progress">
        <div className="h-full bg-gradient-to-r from-primary to-amber-300 transition-[width] duration-1000 ease-linear" style={{ width: `${(pos.offset / pos.length) * 100}%` }} />
      </div>
      <div className="flex justify-between text-xs text-foreground/45 mb-5 tabular-nums">
        <span data-testid="radio-elapsed">{fmt(pos.offset)}</span>
        <span>-{fmt(pos.length - pos.offset)} {T.left}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={r.toggle} data-testid="radio-play-btn" className={`btn-frost-orange inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold ${!r.playing && PLAY_HINT ? "ring-4 ring-primary/40 animate-pulse" : ""}`}>
          {r.loading ? <Loader2 size={16} className="animate-spin" /> : r.playing ? <Pause size={16} /> : <Play size={16} />}
          {r.playing ? T.pause : T.listen}
        </button>
        {r.playing && (
          <button onClick={r.toggleMute} data-testid="radio-mute-btn" aria-label="mute"
            className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-white/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors">
            {r.muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        )}
        <span className="inline-flex items-center gap-1.5 text-xs text-foreground/60" data-testid="radio-listeners">
          <Users size={14} className="text-primary/80" /> <span className="tabular-nums font-semibold text-foreground/85">{listeners}</span> {T.listeners}
        </span>
      </div>
    </div>
  </div>
);

const DebateButton = ({ T, question }) => {
  const navigate = useNavigate();
  const go = () => {
    localStorage.setItem("coccon_pending_proces", question);
    localStorage.setItem("coccon_pending_tab", "proces");
    navigate("/atelier");
  };
  return (
    <button onClick={go} data-testid="radio-debate-btn"
      className="-mt-2 mb-5 inline-flex items-center gap-2 rounded-full border border-primary/35 bg-primary/10 px-4 py-2 text-xs font-semibold text-primary hover:bg-primary/20 hover:-translate-y-0.5 transition-[transform,background-color]">
      <Scale size={14} /> {T.debate}
    </button>
  );
};

const UpNext = ({ T, ep, tt, inSec }) => (
  <div className="mt-5 glass rounded-2xl border border-white/10 p-4 flex items-center gap-4" data-testid="radio-up-next">
    <img src={`/derive/covers/${ep.slug}.jpg`} alt={tt(ep)} className="w-14 h-14 rounded-xl object-cover border border-primary/20" />
    <div className="min-w-0 flex-1">
      <p className="text-[0.65rem] uppercase tracking-[0.22em] text-foreground/45 flex items-center gap-1.5"><SkipForward size={11} /> {T.next} · {fmt(inSec)}</p>
      <p className="font-display text-lg truncate" data-testid="radio-next-title">{tt(ep)}</p>
    </div>
  </div>
);
